import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { LocaleProvider, useLocale } from './context/LocaleContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MotionProvider } from './components/motion/MotionProvider';
import { FirstVisitEntrance } from './components/motion/FirstVisitEntrance';
import { ScrollProgress } from './components/motion/ScrollProgress';
import { BackToTopButton } from './components/motion/BackToTopButton';
import { ThemeProvider } from './theme/ThemeContext';

// Import Public Layout & Shared Components
import PremiumHeader from './components/PremiumHeader';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import ScrollToTop from './components/ScrollToTop';
import RouteTransition from './components/motion/RouteTransition';
import LiquidMobileNav from './components/motion/LiquidMobileNav';

// Helper for lazy loading that catches stale chunk 404 errors and reloads assets smoothly
const safeLazy = (importFn) =>
  lazy(() =>
    importFn().catch((error) => {
      console.warn('Dynamic chunk import failed. Fetching latest application build assets...', error);
      const storageKey = 'pbz_chunk_reload';
      const lastReload = sessionStorage.getItem(storageKey);
      if (!lastReload || Date.now() - parseInt(lastReload, 10) > 10000) {
        sessionStorage.setItem(storageKey, Date.now().toString());
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    })
  );

// Route-level chunks keep public visitors from downloading admin and mapping tools.
const Home = safeLazy(() => import('./pages/public/ProfessionalHome'));
const About = safeLazy(() => import('./pages/public/About'));
const Projects = safeLazy(() => import('./pages/public/Projects'));
const ProjectDetail = safeLazy(() => import('./pages/public/ProjectDetail'));
const Updates = safeLazy(() => import('./pages/public/Updates'));
const UpdateDetail = safeLazy(() => import('./pages/public/UpdateDetail'));
const Gallery = safeLazy(() => import('./pages/public/Gallery'));
const Investment = safeLazy(() => import('./pages/public/Investment'));
const Contact = safeLazy(() => import('./pages/public/Contact'));
const PrivacyPolicy = safeLazy(() => import('./pages/public/PrivacyPolicy'));
const Terms = safeLazy(() => import('./pages/public/Terms'));
const InvestmentDisclaimer = safeLazy(() => import('./pages/public/InvestmentDisclaimer'));

const Login = safeLazy(() => import('./pages/admin/Login'));
const Dashboard = safeLazy(() => import('./pages/admin/Dashboard'));
const DashboardHome = safeLazy(() => import('./pages/admin/DashboardHome'));
const AdminProjects = safeLazy(() => import('./pages/admin/AdminProjects'));
const AdminFloorPlans = safeLazy(() => import('./pages/admin/AdminFloorPlans'));
const AdminUnits = safeLazy(() => import('./pages/admin/AdminUnits'));
const AdminUpdates = safeLazy(() => import('./pages/admin/AdminUpdates'));
const AdminGallery = safeLazy(() => import('./pages/admin/AdminGallery'));
const AdminMediaLibrary = safeLazy(() => import('./pages/admin/AdminMediaLibrary'));
const AdminEnquiries = safeLazy(() => import('./pages/admin/AdminEnquiries'));
const AdminSettings = safeLazy(() => import('./pages/admin/AdminSettings'));
const AdminHomepageBuilder = safeLazy(() => import('./pages/admin/AdminHomepageBuilder'));
const AdminAboutPage = safeLazy(() => import('./pages/admin/AdminAboutPage'));
const AdminInvestmentPage = safeLazy(() => import('./pages/admin/AdminInvestmentPage'));
const AdminUpdatesPage = safeLazy(() => import('./pages/admin/AdminUpdatesPage'));
const AdminConstructionTimeline = safeLazy(() => import('./pages/admin/AdminConstructionTimeline'));
const AdminTimelineTemplates = safeLazy(() => import('./pages/admin/AdminTimelineTemplates'));
const AdminProjectPagesBuilder = safeLazy(() => import('./pages/admin/AdminProjectPagesBuilder'));

// Public Layout wrapper containing responsive header, footer & motion navigation
const PublicLayout = () => {
  const { isRtl } = useLocale();

  return (
    <div className="min-h-screen bg-pbz-dark flex flex-col justify-between text-pbz-lightText selection:bg-pbz-gold selection:text-pbz-dark" dir={isRtl ? 'rtl' : 'ltr'}>
      <ScrollProgress />
      <PremiumHeader />
      <div className="flex-grow flex flex-col">
        <RouteTransition>
          <Outlet />
        </RouteTransition>
      </div>
      <WhatsAppButton />
      <LiquidMobileNav />
      <BackToTopButton />
      <Footer />
    </div>
  );
};

const PageFallback = () => (
  <div className="min-h-[70vh] bg-pbz-dark flex flex-col items-center justify-center px-6 text-center text-pbz-gold text-xs uppercase tracking-[0.25em] gap-3">
    <div className="w-8 h-8 rounded-full border-2 border-[#B99A5B] border-t-transparent animate-spin" />
    <span>Loading Prime Business Zone</span>
  </div>
);

// Auth Guard checks for token & Must Change password attributes
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-pbz-dark flex items-center justify-center text-pbz-gold font-sans text-sm">
        Authenticating Secure Session...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <MotionProvider>
        <FirstVisitEntrance>
          <LocaleProvider>
            <AuthProvider>
              <BrowserRouter>
                <ScrollToTop />
                <Suspense fallback={<PageFallback />}>
                  <Routes>
                    {/* PUBLIC WEB PORTAL */}
                    <Route path="/" element={<PublicLayout />}>
                      <Route index element={<Home />} />
                      <Route path="about" element={<About />} />
                      <Route path="projects" element={<Projects />} />
                      <Route path="projects/:slug" element={<ProjectDetail />} />
                      <Route path="updates" element={<Updates />} />
                      <Route path="updates/:slug" element={<UpdateDetail />} />
                      <Route path="construction-updates" element={<Updates />} />
                      <Route path="construction-updates/:slug" element={<UpdateDetail />} />
                      <Route path="gallery" element={<Gallery />} />
                      <Route path="investment" element={<Investment />} />
                      <Route path="contact" element={<Contact />} />
                      <Route path="privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="terms" element={<Terms />} />
                      <Route path="investment-disclaimer" element={<InvestmentDisclaimer />} />
                    </Route>

                    {/* SECURE ADMIN LOGIN */}
                    <Route path="/admin/login" element={<Login />} />

                    {/* PROTECTED ADMIN DASHBOARD CONSOLE */}
                    <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                    <Route path="/admin/projects" element={<Navigate to="/admin/dashboard/projects" replace />} />
                    <Route path="/admin/projects-page" element={<Navigate to="/admin/dashboard/projects-page" replace />} />
                    <Route path="/admin/homepage" element={<Navigate to="/admin/dashboard/homepage" replace />} />
                    <Route path="/admin/about-page" element={<Navigate to="/admin/dashboard/about-page" replace />} />
                    <Route path="/admin/investment-page" element={<Navigate to="/admin/dashboard/investment-page" replace />} />
                    <Route path="/admin/floor-plans" element={<Navigate to="/admin/dashboard/floor-plans" replace />} />
                    <Route path="/admin/units" element={<Navigate to="/admin/dashboard/units" replace />} />
                    <Route path="/admin/updates" element={<Navigate to="/admin/dashboard/updates" replace />} />
                    <Route path="/admin/updates-page" element={<Navigate to="/admin/dashboard/updates-page" replace />} />
                    <Route path="/admin/gallery" element={<Navigate to="/admin/dashboard/gallery" replace />} />
                    <Route path="/admin/enquiries" element={<Navigate to="/admin/dashboard/enquiries" replace />} />
                    <Route path="/admin/settings" element={<Navigate to="/admin/dashboard/settings" replace />} />
                    <Route path="/admin/projects/timeline" element={<AdminConstructionTimeline />} />
                    <Route path="/admin/settings/timeline-templates" element={<AdminTimelineTemplates />} />
                    <Route path="/admin/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>}>
                      <Route index element={<DashboardHome />} />
                      <Route path="projects" element={<AdminProjects />} />
                      <Route path="projects-page" element={<AdminProjectPagesBuilder />} />
                      <Route path="homepage" element={<AdminHomepageBuilder />} />
                      <Route path="about-page" element={<AdminAboutPage />} />
                      <Route path="investment-page" element={<AdminInvestmentPage />} />
                      <Route path="updates-page" element={<AdminUpdatesPage />} />
                      <Route path="floor-plans" element={<AdminFloorPlans />} />
                      <Route path="units" element={<AdminUnits />} />
                      <Route path="updates" element={<AdminUpdates />} />
                      <Route path="gallery" element={<AdminGallery />} />
                      <Route path="enquiries" element={<AdminEnquiries />} />
                      <Route path="settings" element={<AdminSettings />} />
                    </Route>

                    {/* WILDCARD FALLBACK */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </AuthProvider>
          </LocaleProvider>
        </FirstVisitEntrance>
      </MotionProvider>
    </ThemeProvider>
  );
}

export default App;
